import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Link,
} from '@react-pdf/renderer'
import {
  personalInfo,
  experiences,
  education,
  skillCategories,
  projects,
  socialLinks,
} from '../lib/content'

// Color palette
const colors = {
  primary: '#1e293b',      // slate-800
  secondary: '#475569',    // slate-600
  accent: '#f97316',       // orange-500
  light: '#64748b',        // slate-500
  background: '#f8fafc',   // slate-50
  white: '#ffffff',
  border: '#e2e8f0',       // slate-200
}

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: colors.primary,
    backgroundColor: colors.white,
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: colors.accent,
    paddingBottom: 15,
  },
  name: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 4,
  },
  title: {
    fontSize: 14,
    color: colors.accent,
    marginBottom: 8,
  },
  contactRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    marginTop: 8,
  },
  contactItem: {
    fontSize: 9,
    color: colors.secondary,
  },
  contactLink: {
    fontSize: 9,
    color: colors.accent,
    textDecoration: 'none',
  },
  summary: {
    fontSize: 10,
    color: colors.secondary,
    lineHeight: 1.5,
    marginTop: 10,
  },
  section: {
    marginTop: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: colors.accent,
    marginBottom: 10,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  experienceItem: {
    marginBottom: 14,
  },
  experienceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  experienceTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
  },
  experienceDate: {
    fontSize: 9,
    color: colors.light,
  },
  experienceCompany: {
    fontSize: 10,
    color: colors.accent,
    marginBottom: 3,
  },
  experienceLocation: {
    fontSize: 9,
    color: colors.light,
  },
  experienceDescription: {
    fontSize: 9,
    color: colors.secondary,
    marginTop: 4,
    lineHeight: 1.4,
  },
  achievementsList: {
    marginTop: 4,
    paddingLeft: 10,
  },
  achievementItem: {
    fontSize: 9,
    color: colors.secondary,
    marginBottom: 2,
    lineHeight: 1.4,
  },
  techTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6,
  },
  techTag: {
    fontSize: 7,
    color: colors.secondary,
    backgroundColor: colors.background,
    padding: '2 6',
    borderRadius: 3,
  },
  educationItem: {
    marginBottom: 10,
  },
  educationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  educationDegree: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
  },
  educationField: {
    fontSize: 10,
    color: colors.accent,
    marginTop: 2,
  },
  educationInstitution: {
    fontSize: 9,
    color: colors.secondary,
    marginTop: 2,
  },
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
  },
  skillCategory: {
    width: '48%',
    marginBottom: 10,
  },
  skillCategoryTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 5,
  },
  skillsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  skillItem: {
    fontSize: 8,
    color: colors.secondary,
    backgroundColor: colors.background,
    padding: '2 6',
    borderRadius: 3,
  },
  skillItemExpert: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  skillItemAdvanced: {
    backgroundColor: '#ffedd5',
    color: '#c2410c',
  },
  projectItem: {
    marginBottom: 10,
  },
  projectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  projectName: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
  },
  projectLinks: {
    flexDirection: 'row',
    gap: 8,
  },
  projectLink: {
    fontSize: 8,
    color: colors.accent,
    textDecoration: 'none',
  },
  projectDescription: {
    fontSize: 9,
    color: colors.secondary,
    marginTop: 4,
    lineHeight: 1.4,
  },
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: colors.light,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
  },
})

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Present'
  const [year, month] = dateStr.split('-')
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return month ? `${monthNames[parseInt(month) - 1]} ${year}` : year
}

export function CVDocument() {
  const linkedIn = socialLinks.find(s => s.icon === 'linkedin')
  const github = socialLinks.find(s => s.icon === 'github')

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.name}>{personalInfo.name}</Text>
          <Text style={styles.title}>{personalInfo.title} | {personalInfo.tagline}</Text>
          <View style={styles.contactRow}>
            <Text style={styles.contactItem}>{personalInfo.location}</Text>
            <Link src={`mailto:${personalInfo.email}`} style={styles.contactLink}>
              {personalInfo.email}
            </Link>
            {linkedIn && (
              <Link src={linkedIn.url} style={styles.contactLink}>
                LinkedIn
              </Link>
            )}
            {github && (
              <Link src={github.url} style={styles.contactLink}>
                GitHub
              </Link>
            )}
          </View>
          <Text style={styles.summary}>{personalInfo.about}</Text>
        </View>

        {/* Experience */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>EXPERIENCE</Text>
          {experiences.slice(0, 9).map((exp) => (
            <View key={exp.id} style={styles.experienceItem}>
              <View style={styles.experienceHeader}>
                <Text style={styles.experienceTitle}>{exp.position}</Text>
                <Text style={styles.experienceDate}>
                  {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                </Text>
              </View>
              <Text style={styles.experienceCompany}>
                {exp.company}
                <Text style={styles.experienceLocation}> · {exp.location}</Text>
              </Text>
              {exp.description && (
                <Text style={styles.experienceDescription}>{exp.description}</Text>
              )}
              {exp.achievements.length > 0 && (
                <View style={styles.achievementsList}>
                  {exp.achievements.slice(0, 3).map((achievement, idx) => (
                    <Text key={idx} style={styles.achievementItem}>• {achievement}</Text>
                  ))}
                </View>
              )}
              <View style={styles.techTags}>
                {exp.technologies.slice(0, 6).map((tech) => (
                  <Text key={tech} style={styles.techTag}>{tech}</Text>
                ))}
              </View>
            </View>
          ))}
        </View>

        <Text style={styles.footer}>
          Generated from leveque.es · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Text>
      </Page>

      {/* Page 2 - More Experience + Education */}
      <Page size="A4" style={styles.page}>
        {/* Remaining Experience */}
        {experiences.length > 9 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>EXPERIENCE (continued)</Text>
            {experiences.slice(9).map((exp) => (
              <View key={exp.id} style={styles.experienceItem}>
                <View style={styles.experienceHeader}>
                  <Text style={styles.experienceTitle}>{exp.position}</Text>
                  <Text style={styles.experienceDate}>
                    {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                  </Text>
                </View>
                <Text style={styles.experienceCompany}>
                  {exp.company}
                  <Text style={styles.experienceLocation}> · {exp.location}</Text>
                </Text>
                {exp.achievements.length > 0 && (
                  <View style={styles.achievementsList}>
                    {exp.achievements.slice(0, 2).map((achievement, idx) => (
                      <Text key={idx} style={styles.achievementItem}>• {achievement}</Text>
                    ))}
                  </View>
                )}
                <View style={styles.techTags}>
                  {exp.technologies.slice(0, 5).map((tech) => (
                    <Text key={tech} style={styles.techTag}>{tech}</Text>
                  ))}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Education */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>EDUCATION</Text>
          {education.map((edu) => (
            <View key={edu.id} style={styles.educationItem}>
              <View style={styles.educationHeader}>
                <View>
                  <Text style={styles.educationDegree}>{edu.degree}</Text>
                  <Text style={styles.educationField}>{edu.field}</Text>
                  <Text style={styles.educationInstitution}>
                    {edu.institution} · {edu.location}
                  </Text>
                </View>
                <Text style={styles.experienceDate}>{edu.startDate} - {edu.endDate}</Text>
              </View>
            </View>
          ))}
        </View>

        <Text style={styles.footer}>
          Generated from leveque.es · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Text>
      </Page>

      {/* Page 3 - Skills + Projects */}
      <Page size="A4" style={styles.page}>
        {/* Skills */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SKILLS</Text>
          <View style={styles.skillsGrid}>
            {skillCategories.map((category) => (
              <View key={category.id} style={styles.skillCategory}>
                <Text style={styles.skillCategoryTitle}>{category.name}</Text>
                <View style={styles.skillsList}>
                  {category.skills.map((skill) => {
                    const skillStyle = skill.level === 'expert'
                      ? [styles.skillItem, styles.skillItemExpert]
                      : skill.level === 'advanced'
                        ? [styles.skillItem, styles.skillItemAdvanced]
                        : styles.skillItem
                    return (
                      <Text key={skill.name} style={skillStyle}>
                        {skill.name}
                      </Text>
                    )
                  })}
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Projects */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>PROJECTS</Text>
          {projects.map((project) => (
            <View key={project.id} style={styles.projectItem}>
              <View style={styles.projectHeader}>
                <Text style={styles.projectName}>{project.name}</Text>
                <View style={styles.projectLinks}>
                  {project.githubUrl && (
                    <Link src={project.githubUrl} style={styles.projectLink}>
                      GitHub
                    </Link>
                  )}
                  {project.url && (
                    <Link src={project.url} style={styles.projectLink}>
                      {project.url.replace(/^https?:\/\//, '')}
                    </Link>
                  )}
                </View>
              </View>
              <Text style={styles.projectDescription}>{project.description}</Text>
              <View style={styles.techTags}>
                {project.technologies.map((tech) => (
                  <Text key={tech} style={styles.techTag}>{tech}</Text>
                ))}
              </View>
            </View>
          ))}
        </View>

        <Text style={styles.footer}>
          Generated from leveque.es · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Text>
      </Page>
    </Document>
  )
}
